import type { ProductCategory, ProductInterest } from '@/api/productCatalog'

/**
 * One category as a search leaves it: the category itself, the interests still worth showing, and
 * how many it has altogether — so a card can say "3 von 8 Interessen" rather than quietly
 * claiming the category only ever had three.
 */
export type CatalogMatch = {
  category: ProductCategory
  interests: ProductInterest[]
  total: number
}

/**
 * The catalogue narrowed to what somebody typed.
 *
 * Both levels match, because both are what people look for: "Backtechnik" is a category, but
 * "Ofentechnik" is an interest and whoever is looking for it rarely remembers which category it
 * sits under. A category whose **own name** matches keeps all of its interests — the search found
 * the category, so hiding what is in it would answer a different question. A category that matches
 * only through its interests shows those.
 *
 * Done in the page rather than in the backend, and that is not an inconsistency with the other
 * screens: the catalogue arrives whole in one request because the lead picker needs all of it, so
 * there is nothing a round trip could add except the round trip.
 */
export function searchCatalog(categories: ProductCategory[], search: string): CatalogMatch[] {
  const term = search.trim().toLocaleLowerCase()
  if (term === '') {
    return categories.map((category) => whole(category))
  }

  return categories.flatMap((category) => {
    if (matches(category.name, term)) {
      return [whole(category)]
    }
    const interests = category.interests.filter((interest) => matches(interest.name, term))
    return interests.length > 0
      ? [{ category, interests, total: category.interests.length }]
      : []
  })
}

function whole(category: ProductCategory): CatalogMatch {
  return { category, interests: category.interests, total: category.interests.length }
}

function matches(name: string, term: string): boolean {
  return name.toLocaleLowerCase().includes(term)
}
